// The design system's components (design/components/bundle.js) as typed React: identical markup and class names,
// so design/components/bundle.css styles them unchanged. Differences: strings are localized, interactive
// components are controlled by the app, and times are epoch milliseconds.
import { useEffect, useState, type ButtonHTMLAttributes, type CSSProperties, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { useFmt, useT } from '../store/hooks.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { AGENT_LOGOS } from './logos.ts';

export const cx = (...names: (string | false | null | undefined)[]): string => names.filter(Boolean).join(' ');

// MARK: Button

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'signal' | 'primary' | 'tinted' | 'plain' | 'glass' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  /** Icon-only capsule; pass aria-label. */
  iconOnly?: boolean;
}

export function Button({ variant = 'tinted', size = 'md', icon, iconOnly, className, children, type = 'button', ...rest }: ButtonProps) {
  const iconSize = size === 'lg' ? 20 : size === 'sm' ? 14 : 18;
  return (
    <button type={type} {...rest} className={cx('tl-btn', `tl-btn--${variant}`, `tl-btn--${size}`, iconOnly && 'tl-btn--icon', className)}
      title={rest.title ?? (iconOnly ? rest['aria-label'] : undefined)}>
      {icon ? <Icon name={icon} size={iconSize} /> : null}
      {iconOnly ? null : children}
    </button>
  );
}

// MARK: Segmented control

export interface SegmentedControlProps<T extends string> {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  glass?: boolean;
  label?: string;
  className?: string;
}

export function SegmentedControl<T extends string>({ options, value, onChange, size, glass, label, className }: SegmentedControlProps<T>) {
  return (
    <div className={cx('tl-seg', size === 'sm' && 'tl-seg--sm', glass && 'tl-seg--glass', className)} role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={cx('tl-seg__opt', o.value === value && 'is-on')}
          onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// MARK: Status

export type PillStateName = 'working' | 'running' | 'waiting' | 'permission' | 'paused' | 'idle' | 'done' | 'error';

const STATES: Record<PillStateName, { icon: IconName | 'live'; tone: string; label: string }> = {
  working: { icon: 'live', tone: 'signal', label: 'Arbeitet' },
  running: { icon: 'live', tone: 'signal', label: 'Läuft' },
  waiting: { icon: 'hourglass', tone: 'waiting', label: 'Wartet auf Eingabe' },
  permission: { icon: 'hourglass', tone: 'waiting', label: 'Wartet auf Freigabe' },
  paused: { icon: 'pause', tone: 'neutral', label: 'Pausiert' },
  idle: { icon: 'moon', tone: 'neutral', label: 'Inaktiv' },
  done: { icon: 'check', tone: 'neutral', label: 'Fertig' },
  error: { icon: 'alert', tone: 'danger', label: 'Fehler' },
};

export function LiveDot() {
  return <span className="tl-live" aria-hidden="true" />;
}

export function StatusPill({ state, label, size, className }: { state: PillStateName; label?: string; size?: 'sm' | 'md'; className?: string }) {
  const t = useT();
  const s = STATES[state];
  return (
    <span className={cx('tl-pill', `tl-pill--${s.tone}`, size === 'sm' && 'tl-pill--sm', className)}>
      {s.icon === 'live' ? <LiveDot /> : <Icon name={s.icon} size={size === 'sm' ? 12 : 14} strokeWidth={2} />}
      <span>{label ?? t(s.label)}</span>
    </span>
  );
}

export function statusLabel(t: (k: string) => string, state: PillStateName): string {
  return t(STATES[state].label);
}

export function ProjectChip({ name, color, plain, className }: { name: string; color: number; plain?: boolean; className?: string }) {
  return (
    <span className={cx('tl-chip', plain && 'tl-chip--plain', className)} title={name}>
      <span className="tl-dot" style={{ background: `var(--project-${color || 1})` }} />
      <span className="tl-chip__name">{name}</span>
    </span>
  );
}

export function SourceBadge({ source, label, className }: { source: 'human' | 'agent'; label?: string; className?: string }) {
  const t = useT();
  const agent = source === 'agent';
  return (
    <span className={cx('tl-src', `tl-src--${agent ? 'agent' : 'human'}`, className)}>
      <Icon name={agent ? 'terminal' : 'user'} size={14} strokeWidth={2} />
      <span>{label ?? (agent ? t('Agent') : t('Manuell'))}</span>
    </span>
  );
}

/** The provider's tile: its logo where there is one (`provider` id), else the two letters. */
export function Monogram({ text, size, provider }: { text: string; size?: 'sm'; provider?: string }) {
  const logo = provider ? AGENT_LOGOS[provider] : undefined;
  return (
    <span className={cx('tl-mono-tile', size === 'sm' && 'tl-mono-tile--sm')} aria-hidden="true">
      {!logo ? text : logo.mask
        ? <span className="agent-logo agent-logo--mask" style={{ maskImage: `url(${logo.url})`, WebkitMaskImage: `url(${logo.url})` }} />
        : <img className="agent-logo" src={logo.url} alt="" />}
    </span>
  );
}

// MARK: Time

/** Timers tick without animation; the value is spoken as words, never announced every second. */
export function TimerDisplay({ ms, state = 'stopped', size = 'hero', className }: { ms: number; state?: 'running' | 'paused' | 'stopped'; size?: 'hero' | 'lg' | 'sm'; className?: string }) {
  const fmt = useFmt();
  const text = fmt.clock(ms);
  const i = text.lastIndexOf(':');
  return (
    <span className={cx('tl-timer', `tl-timer--${size}`, `tl-timer--${state}`, className)} role="timer" aria-label={fmt.spoken(ms)}>
      <span>{text.slice(0, i)}</span>
      <span className="tl-timer__sec">{text.slice(i)}</span>
    </span>
  );
}

export function StatTile({ label, value, unit, accent, hint, className }: { label: string; value: string; unit?: string; accent?: 'human' | 'agent' | 'signal'; hint?: string; className?: string }) {
  return (
    <div className={cx('tl-card', 'tl-stat', className)}>
      <div className="tl-stat__label">
        {accent ? <span className="tl-dot" style={{ background: `var(--${accent})` }} /> : null}
        {label}
      </div>
      <div className="tl-stat__value">
        {value}
        {unit ? <span className="tl-stat__unit">{unit}</span> : null}
      </div>
      {hint ? <div className="tl-stat__foot"><span className="tl-stat__hint">{hint}</span></div> : null}
    </div>
  );
}

// MARK: Tooltip (hover layer)

export interface Tip {
  show(e: MouseEvent<HTMLElement>, content: ReactNode): void;
  hide(): void;
  node: ReactNode;
}

export function useTip(): Tip {
  const [tip, setTip] = useState<{ x: number; y: number; content: ReactNode } | null>(null);
  return {
    show(e, content) {
      const host = e.currentTarget.closest('[data-tl-tiphost]');
      if (!host) return;
      const r = host.getBoundingClientRect();
      const t = e.currentTarget.getBoundingClientRect();
      setTip({ x: t.left + t.width / 2 - r.left, y: t.top - r.top, content });
    },
    hide() { setTip(null); },
    node: tip ? <div className="tl-tip" role="tooltip" style={{ left: tip.x, top: tip.y }}>{tip.content}</div> : null,
  };
}

// MARK: Split bar

export function SplitBar({ human, agent, legend, height }: { human: number; agent: number; legend?: boolean; height?: number }) {
  const t = useT();
  const fmt = useFmt();
  const tip = useTip();
  const total = human + agent || 1;
  const pct = (v: number) => `${Math.round((v / total) * 100)} %`;
  const bar = (
    <div className="tl-split" role="img" aria-label={`${t('Eigene Zeit')} ${fmt.duration(human)}, ${t('Agent-Zeit')} ${fmt.duration(agent)}`} data-tl-tiphost=""
      style={height ? { height } : undefined}>
      {human > 0 ? <div className="tl-split__seg tl-split__seg--human" style={{ width: `${(human / total) * 100}%` }}
        onMouseEnter={(e) => tip.show(e, [<b key="b">{t('Eigene Zeit')}</b>, <span key="s">{`${fmt.duration(human)} · ${pct(human)}`}</span>])} onMouseLeave={tip.hide} /> : null}
      {agent > 0 ? <div className="tl-split__seg tl-split__seg--agent" style={{ width: `${(agent / total) * 100}%` }}
        onMouseEnter={(e) => tip.show(e, [<b key="b">{t('Agent-Zeit')}</b>, <span key="s">{`${fmt.duration(agent)} · ${pct(agent)}`}</span>])} onMouseLeave={tip.hide} /> : null}
      {tip.node}
    </div>
  );
  if (!legend) return bar;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {bar}
      <div className="tl-legend">
        <span className="tl-legend__item"><span className="tl-legend__sw" style={{ background: 'var(--human)' }} />{`${t('Eigene Zeit')} ${fmt.duration(human)}`}</span>
        <span className="tl-legend__item"><span className="tl-legend__sw" style={{ background: 'var(--agent)' }} />{`${t('Agent-Zeit')} ${fmt.duration(agent)}`}</span>
      </div>
    </div>
  );
}

// MARK: Rows

export interface ProjectRowProps {
  name: string;
  client?: string | undefined;
  color: number;
  humanMs: number;
  agentMs: number;
  /** Billed: the union of your time and agent time. */
  mergedMs: number;
  amount?: string | undefined;
  running?: boolean;
  compact?: boolean;
  className?: string;
}

export function ProjectRow({ name, client, color, humanMs, agentMs, mergedMs, amount, running, compact, className }: ProjectRowProps) {
  const t = useT();
  const fmt = useFmt();
  const dot = <span className="tl-dot" style={{ background: `var(--project-${color || 1})` }} />;
  if (compact) {
    return (
      <div className={cx('tl-row', 'tl-prow', 'tl-prow--compact', running && 'tl-row--running', className)} role="row">
        <div className="tl-prow__name">
          {dot}
          <div className="tl-prow__body">
            <div className="tl-row__title">{name}</div>
            <SplitBar human={humanMs} agent={agentMs} />
            <div className="tl-row__sub">{t('Eigene %@ · Agents %@', fmt.duration(humanMs), fmt.duration(agentMs))}</div>
          </div>
        </div>
        <div className="tl-row__num">
          {amount ?? fmt.duration(mergedMs)}
          <small>{amount ? t('%@ abgerechnet', fmt.duration(mergedMs)) : client ?? t('abgerechnet')}</small>
        </div>
      </div>
    );
  }
  return (
    <div className={cx('tl-row', 'tl-prow', running && 'tl-row--running', className)} role="row">
      <div className="tl-prow__name">
        {dot}
        <div><div className="tl-row__title">{name}</div><div className="tl-row__sub">{client}</div></div>
      </div>
      <SplitBar human={humanMs} agent={agentMs} />
      <div className="tl-row__num">{fmt.duration(humanMs)}<small>{t('Eigene')}</small></div>
      <div className="tl-row__num">{fmt.duration(agentMs)}<small>{t('Agents')}</small></div>
      <div className="tl-row__num">{amount ?? fmt.duration(mergedMs)}<small>{amount ? t('%@ abgerechnet', fmt.duration(mergedMs)) : t('abgerechnet')}</small></div>
    </div>
  );
}

export interface EntryRowProps {
  from: string;
  to?: string | undefined;
  description: string;
  project?: { name: string; color: number } | undefined;
  source: 'human' | 'agent';
  sourceLabel?: string | undefined;
  billable?: boolean;
  ms: number;
  running?: boolean;
  tags?: string[];
  className?: string;
}

export function EntryRow({ from, to, description, project, source, sourceLabel, billable, ms, running, tags, className }: EntryRowProps) {
  const t = useT();
  const fmt = useFmt();
  return (
    <div className={cx('tl-row', 'tl-erow', running && 'tl-row--running', className)} role="row">
      <div className="tl-erow__time">{`${from}–${running ? t('jetzt') : to ?? ''}`}</div>
      <div className="tl-erow__desc">
        <div className="tl-row__title">{description || t('Ohne Beschreibung')}</div>
        <div className="tl-erow__tags">
          {project ? <ProjectChip name={project.name} color={project.color} plain /> : null}
          <SourceBadge source={source} label={sourceLabel} />
          {tags?.map((tag) => <span key={tag} className="tl-caption">#{tag}</span>)}
        </div>
      </div>
      <span className={cx('tl-erow__bill', billable && 'is-on')} title={billable ? t('Abrechenbar') : t('Nicht abrechenbar')}>
        <Icon name="euro" size={16} label={billable ? t('Abrechenbar') : t('Nicht abrechenbar')} />
      </span>
      <div className="tl-row__num">{running ? <TimerDisplay ms={ms} state="running" size="sm" /> : fmt.duration(ms)}</div>
    </div>
  );
}

// MARK: Chrome

export interface GlassPanelProps extends HTMLAttributes<HTMLElement> {
  variant?: 'regular' | 'strong';
  shape?: 'panel' | 'capsule';
  flat?: boolean;
  as?: 'div' | 'nav' | 'aside' | 'section' | 'header';
}

export function GlassPanel({ variant, shape = 'panel', flat, as: Tag = 'div', className, children, ...rest }: GlassPanelProps) {
  return (
    <Tag {...rest} className={cx('tl-glass', variant === 'strong' && 'tl-glass--strong', `tl-glass--${shape}`, flat && 'tl-glass--flat', className)}>
      {children}
    </Tag>
  );
}

export interface TabItem {
  id: string;
  icon: IconName;
  label: string;
  live?: boolean;
}

export function TabBar({ items, value, onChange, label, className, style }: { items: TabItem[]; value: string; onChange: (id: string) => void; label?: string; className?: string; style?: CSSProperties }) {
  return (
    <GlassPanel shape="capsule" className={cx('tl-tabbar', className)} role="tablist" aria-label={label} style={style}>
      {items.map((it) => (
        <button key={it.id} type="button" role="tab" aria-selected={it.id === value} className={cx('tl-tab', it.id === value && 'is-on')} onClick={() => onChange(it.id)}>
          <span className="tl-tab__icon"><Icon name={it.icon} size={22} />{it.live ? <LiveDot /> : null}</span>
          <span>{it.label}</span>
        </button>
      ))}
    </GlassPanel>
  );
}

export interface SidebarItem {
  id: string;
  label: string;
  icon?: IconName;
  color?: number;
  count?: string | number | undefined;
  live?: boolean;
  warning?: boolean;
}

export interface SidebarNavProps {
  sections: { label?: string; items: SidebarItem[] }[];
  value: string | null;
  onChange: (id: string) => void;
  header?: ReactNode;
  footer?: ReactNode;
  flat?: boolean;
  label?: string;
  className?: string;
  style?: CSSProperties;
}

export function SidebarNav({ sections, value, onChange, header, footer, flat, label, className, style }: SidebarNavProps) {
  const t = useT();
  return (
    <GlassPanel as="nav" className={cx('tl-side', className)} flat={flat} aria-label={label} style={style}>
      {header}
      {sections.map((sec, i) => (
        <div key={i} className="tl-side__sec">
          {sec.label ? <div className="tl-label">{sec.label}</div> : null}
          {sec.items.map((it) => (
            <button key={it.id} type="button" className={cx('tl-side__item', it.id === value && 'is-on')} aria-current={it.id === value ? 'page' : undefined}
              onClick={() => onChange(it.id)}>
              {it.color ? <span className="tl-dot" style={{ background: `var(--project-${it.color})`, margin: '0 5px' }} /> : <Icon name={it.icon ?? 'folder'} size={18} />}
              <span className="tl-side__name">{it.label}</span>
              {it.live ? <LiveDot /> : null}
              {it.warning ? <span className="tl-side__warn"><Icon name="alert" size={14} strokeWidth={2} label={t('Fehler')} /></span> : null}
              {it.count !== undefined ? <span className="tl-side__count">{it.count}</span> : null}
            </button>
          ))}
        </div>
      ))}
      {footer}
    </GlassPanel>
  );
}

// MARK: Integration tile

export function IntegrationTile({ name, mono, provider, description, methods, status, detail, action, className }: {
  name: string; mono: string; provider?: string; description: string; methods?: string[]; status: 'connected' | 'available' | 'error' | 'soon'; detail?: string;
  action?: ReactNode; className?: string;
}) {
  const t = useT();
  const statusLine = status === 'connected' ? [<LiveDot key="d" />, <span key="t">{detail ?? t('Verbunden')}</span>]
    : status === 'error' ? [<StatusPill key="p" state="error" size="sm" label={t('Hook-Fehler')} />, <span key="t">{detail ?? ''}</span>]
    : status === 'soon' ? [<span key="t">{detail ?? t('Bald verfügbar')}</span>]
    : [<span key="t">{detail ?? t('Nicht verbunden')}</span>];
  return (
    <article className={cx('tl-card', 'tl-int', status === 'soon' && 'tl-int--soon', className)}>
      <div className="tl-int__head">
        <Monogram text={mono} {...(provider ? { provider } : {})} />
        <div className="tl-int__who"><div className="tl-int__name">{name}</div><div className="tl-int__desc">{description}</div></div>
      </div>
      <div className="tl-int__status">{statusLine}</div>
      <div className="tl-int__foot">
        <div className="tl-int__methods">{(methods ?? []).map((m) => <span key={m} className="tl-int__method">{m}</span>)}</div>
        {action}
      </div>
    </article>
  );
}

/** Keeps a value in sync with a prop but lets the user edit it (drafts in forms). */
export function useDraft<T>(value: T): [T, (v: T) => void] {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return [draft, setDraft];
}
